import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { isEmptyValue } from '../../../lib/validation';
import FieldTypes from '../../../constants/FieldTypes';
import FieldTitle from './FieldTitle';
import { hexToRGBA } from '../../../lib/colors';

import FieldModes from '../../../constants/FieldModes';
import PageLayoutModes from '../../../constants/PageLayoutModes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';
import FormStylesOptions from '../../../constants/FormStylesOptions';

import Tooltip from '../../misc/Tooltip';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Only use height when you don't need height measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
  z-index: 9999999;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TextareaField extends PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    value: PropTypes.string,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,
    height: PropTypes.number, //Important for re-calculating line heights

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,
    lineHeight: PropTypes.number,
    primaryColor: PropTypes.string,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    //Used for calculating width and height of element
    measure: PropTypes.bool,
    onMeasureCompleted: PropTypes.func,

  }

  static defaultProps = {
    lineHeight: 10
  }

  constructor(props) {

    super(props);

    const { value } = this.props;

    this.state = {
      focused: false,
      changes: value,
    };
  }

  componentDidUpdate(prevProps) {

    const { changes, wrapperRef } = this.state;

    const { fieldMode, value, fieldId, fieldIdentifier, fieldPositionId, onMeasureCompleted, measure, measuredHeight, measuredOn, updatedOn } = this.props;

    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });

    const remeasureRequired = !measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn);
    const isMeasurable = measure && fieldMode === FieldModes.pdf;
    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.textarea);

    //Important Note: Vanilla JS initally has a height of 0. We need to ensure there is a height before we can measure. This is why we check for the bounding client rect.
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    if (wrapperRef && isMeasurable && onMeasureCompleted && hasPopulatedValue && refHeightAvailable && remeasureRequired) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, { height: refHeightAvailable, measuredOn: new Date().getTime() });
      });
    }

  }

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes, wrapperRef } = this.state;

    const {

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      tabIndex,
      height,
      measure,

      //SDK Styles
      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
      lineHeight,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      fieldMode,
      fieldPresentationMode,
      pageLayoutMode,

    } = this.props;

    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.textarea);
    const isFillablePDFForm = pageLayoutMode === PageLayoutModes.float && fieldPresentationMode === FieldPresentationModes.transparent;
    const TextareaNode = isFillablePDFForm ? FillablePDFTextarea : StandardTextarea;

    /**
     * Why do we have to check for measure and fieldMode here?
     * 
     * This is because the textarea field is only measurable in PDF mode. The only way
     * we can measure the textarea field is by using a span element and measuring the 
     * height of the span element. This allows measurements to be taken but also removes
     * the ability to edit the textarea field. Since editing is not allowed in PDF mode, we
     * don't need to support editing. Displaying all the text is more important there. 
     * 
     * This is the current trade-off we have to make.
     */
    const isMeasurable = measure && fieldMode === FieldModes.pdf;

    return (
      <StyledWrapper
        {...{ margin, measure: isMeasurable && hasPopulatedValue }}
        ref={(e) => {
          if (e && !wrapperRef) this.setState({ wrapperRef: e });
        }}
      >
        <FieldTitle
          {...{
            title,
            titleDisplay,
            titleFontSize,
            titleFontColor,
            titleTextAlign,
            titleFontStyle,
            titleFontWeight,
            titleTextTransform,
            titleTextDecoration,
          }}
        />
        {this.getTooltip()}
        <TextareaNode
          measure={isMeasurable}
          tabIndex={tabIndex}
          value={changes}
          {...{

            id: fieldPositionId,
            fieldId,
            disabled,
            fieldMode,
            hideLines: disabled || !isFillablePDFForm || fieldMode !== FieldModes.edit,
            height,

            primaryColor,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            lineHeight,
            tipVisible,
            tipTitle,
            tipDescription,
            fieldPresentationMode,
            pageLayoutMode,
          }}
          onChange={(e) => {
            this.setState({ changes: e.target.value });
          }}
          onClick={() => {
            if (!focused) this.setState({ focused: true });
          }}
          onFocus={(e) => {
            if (!disabled && onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
          }}
          onBlur={(e) => {
            const nextValue = e.target.value;

            if (focused) this.setState({ focused: false });

            if (!disabled && onChange && nextValue !== value) onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);

            if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

          }}

        />
      </StyledWrapper>
    );
  }

  getTooltip = () => {

    const { fieldId, tipTitle, tipDescription, tipVisible, disabled } = this.props;

    if (disabled) return null;

    return (
      <StyledTooltipWrapper>
        <Tooltip
          fieldId={fieldId}
          title={tipTitle}
          description={tipDescription}
          tipVisible={tipVisible}
        />
      </StyledTooltipWrapper>
    );

  };

}

const StyledTextareaFieldWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
`;

const textareaStyles = (props) => {

  let overflowY = props.textOverflow === FormStylesOptions.textOverflowEllipsis ? 'hidden' : 'auto';
  if (props.fieldMode === FieldModes.readOnly || props.fieldMode === FieldModes.pdf) overflowY = 'hidden';

  return `
    width: 100%;
    height: 100%;
    font-size:${props.fontSize}px !important;
    color: ${props.fontColor};
    font-style: ${props.fontStyle};
    font-weight: ${props.fontWeight};
    text-align: ${props.textAlign};
    text-transform: ${props.textTransform};
    text-decoration: ${props.textDecoration};
    ${props.textOverflow ? `text-overflow: ${props.textOverflow};` : ''}
    border-radius: ${props.borderRadius}px;
    box-sizing: border-box;
    border: ${props.border};
    padding:  ${props.padding}px;
    line-height: ${props.lineHeightPixels !== undefined ? `${props.lineHeightPixels}px` : 'initial'} !important;
    background-color: transparent;
    resize: none;
    transition: border 0.15s ease-in-out;
    z-index: 1;

    /*
      Without this line, when text wrapping is set to hide (textOverflow :ellipsis) scroll bar appears within textarea in readOnly mode
    */
    overflow-y: ${overflowY};

    &:focus, &:focus-visible {
      outline: unset;
      border: ${props.borderWidth}px solid ${props.primaryColor};
    }

    &:hover {
      ${props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`};
    }

  `;
};

const StyledStandardTextarea = styled.textarea`
  ${(props) => textareaStyles(props)}
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
`;

const StyledStandardTextareaMeasureable = styled.span`
  ${(props) => textareaStyles(props)}
  white-space: pre-line;
  overflow-y: visible;
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
`;

/**
 * This component is used to render a text area for standard forms.
 */
class StandardTextarea extends PureComponent {

  render() {

    const { id, borderWidth, borderColor, value, measure, onRef } = this.props;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const textareaNode = !measure ? (
      <StyledStandardTextarea
        ref={onRef}
        data-testid={`${id}-textarea-field`}
        {...this.props}
        border={border}
      />
    ) : (
      <StyledStandardTextareaMeasureable
        ref={onRef}
        data-testid={`${id}-textarea-field-mesureable`}
        {...this.props}
        border={border}
      >
        {value}
      </StyledStandardTextareaMeasureable>
    );

    return (
      <StyledTextareaFieldWrapper>
        {textareaNode}
      </StyledTextareaFieldWrapper>
    );

  }
}

const StyledFillablePDFTextarea = styled.textarea`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${(props) => textareaStyles(props)}
`;

const StyledFillablePDFTextareaMeasureable = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${(props) => textareaStyles(props)}
  white-space: pre-line;
  overflow-y: visible;
`;

const StyledFillablePDFTextareaBackground = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
`;

/**
 * This components main purpose is to isolate the input
 * styles into one location and not require style updates
 * all over the place when we update it in the future.
 */
class FillablePDFTextarea extends PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      lineHeightPixels: 0
    };
  }

  componentDidUpdate(prevProps, prevState) {

    const { lineHeight } = this.props;
    const { wrapperRef } = this.state;

    const wrapperHeight = wrapperRef.offsetHeight;
    const nextLineHeightPixels = wrapperHeight * (lineHeight / 100);

    if (nextLineHeightPixels !== prevState.lineHeightPixels) {
      this.setState({ lineHeightPixels: nextLineHeightPixels });
    }

  }

  render() {

    const { lineHeightPixels, wrapperRef } = this.state;

    const {
      id,
      hideLines,
      height,
      fieldId,
      lineHeight,
      borderWidth,
      borderColor,
      borderRadius,
      backgroundColor,
      fontColor,
      textOverflow,
      value,
      fieldMode,
      onRef
    } = this.props;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const inputNode = (fieldMode === FieldModes.readOnly || fieldMode === FieldModes.pdf) && textOverflow !== FormStylesOptions.textOverflowEllipsis ? (
      <StyledFillablePDFTextareaMeasureable
        ref={onRef}
        data-testid={`${id}-textarea-field`}
        {...this.props}
        border={border}
        lineHeightPixels={lineHeightPixels}
      >
        {value}
      </StyledFillablePDFTextareaMeasureable>
    ) : (
      <StyledFillablePDFTextarea
        ref={onRef}
        data-testid={`${id}-textarea-field`}
        {...this.props}
        border={border}
        lineHeightPixels={lineHeightPixels}
      />
    );

    return (
      <StyledTextareaFieldWrapper
        ref={(e) => {
          if (e && !wrapperRef) {
            if (onRef) onRef(e);
            this.setState({ wrapperRef: e });
          }
        }}
      >
        <StyledFillablePDFTextareaBackground
          data-testid={`${id}-textarea-field-background`}
          {...{
            backgroundColor,
            borderRadius,
          }}
        />
        {hideLines ? null : <TextareaFieldLines {...{ id, fieldId, fontColor, height, lineHeight, borderRadius }} />}
        {inputNode}
      </StyledTextareaFieldWrapper>
    );

  }
}

const StyledTextareaFieldLinesWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.borderRadius}px;
  overflow: hidden;
`;

const StyledTextareaFieldLine = styled.div`
  height: ${(props) => (`${props.height}px`)};
  width: 100%;
  border-bottom: 1px solid ${(props) => props.fontColor};
`;

class TextareaFieldLines extends PureComponent {

  constructor(props) {
    super(props);
    this.state = { lines: 0 };
  }

  static propTypes = {
    id: PropTypes.string,
    fontColor: PropTypes.string,
    borderRadius: PropTypes.number,
    lineHeight: PropTypes.number,
    onRef: PropTypes.func //Only used for testing purposes
  }

  static defaultProps = {
    lineHeight: 10
  }

  componentDidUpdate(prevProps, prevState) {

    const { lineHeight, height } = this.props;
    const { wrapperRef } = this.state;

    const wrapperHeight = wrapperRef.offsetHeight;
    const nextLineHeights = wrapperHeight * (lineHeight / 100);
    const nextLines = Math.round(wrapperHeight / nextLineHeights);

    if (
      height !== prevProps.height
      || nextLines !== prevState.lines
      || lineHeight !== prevProps.lineHeight
    ) {
      this.setState({ lines: nextLines, lineHeights: nextLineHeights });
    }

  }

  render() {

    const { id, borderRadius, onRef } = this.props;
    const { wrapperRef, lines, lineHeights } = this.state;

    const lineNodes = [];

    for (let i = 0; i < lines; i++) {
      lineNodes.push(
        <StyledTextareaFieldLine
          key={i}
          data-testid={`${id}-textarea-field-lines-line-${i}`}
          fontColor={this.getFontColor()}
          height={lineHeights}
        />
      );
    }

    return (
      <StyledTextareaFieldLinesWrapper
        {...{ borderRadius }}
        ref={(e) => {
          if (e && !wrapperRef) {
            if (onRef) onRef(e);
            this.setState({ wrapperRef: e });
          }
        }}
        data-testid={`${id}-textarea-field-lines`}
      >
        {lineNodes}
      </StyledTextareaFieldLinesWrapper>
    );
  }

  getFontColor = () => {

    const { fontColor } = this.props;
    return hexToRGBA(fontColor, '0.3');

  }

}

export { TextareaFieldLines, StandardTextarea, FillablePDFTextarea };

export default TextareaField;
